import React, { useRef, useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import logo from "../assets/img/logo_hob.png";
import video from "../assets/img/Video_Content Creation_HoB.mp4";

import ruckstuhl from "../assets/brandLogos/ruckstuhl.svg";
import bossard from "../assets/brandLogos/bossard.svg";
import alpstudios from "../assets/brandLogos/alpstudios.svg";
import maru from "../assets/brandLogos/maru.svg";
import clyde from "../assets/brandLogos/clyde.svg";
import liini from "../assets/brandLogos/liini.svg";
import fibula from "../assets/brandLogos/fibula.svg";
import spitex from "../assets/brandLogos/spitex.svg";
import VideoCarousel from "../components/VideoCarousel";

function Subscribe() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const [progress, setProgress] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    AOS.init({
      duration: 800, // animation duration
      easing: "ease-in-out",
      once: false, // allows animation to happen more than once
      mirror: true, // animate out when scrolling past and animate back when scrolling back
    });
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animationRef.current);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      updateProgress();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const updateProgress = () => {
    if (!videoRef.current) return;
    const current = videoRef.current.currentTime;
    const duration = videoRef.current.duration || 1;
    setProgress((current / duration) * 100);
    animationRef.current = requestAnimationFrame(updateProgress);
  };

  useEffect(() => {
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  const scrollRef = useRef(null);

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <>
      <div style={{ position: "relative", overflowX: "hidden" }}>
        {/* HERO SECTION */}
        <section
          className="text-center container"
          style={{ padding: "100px 0" }}
        >
          <div className="mb-5 pb-3" data-aos="fade-up">
            <img
              src={logo}
              alt="Hob Studio Logo"
              style={{ width: "50px", marginBottom: "0px" }}
            />
            <h3 className="gradient-text">Your creative partner!</h3>
          </div>

          <h2 data-aos="fade-up" data-aos-delay="100">
            Unlimited Creativity. One Simple Subscription
          </h2>
          <p
            className="fw-semibold"
            style={{ color: "#6d6d6d" }}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Complete post-production services for agencies & brands
            <br />
            Effortless and subscription-based.
          </p>

          <p
            className="fw-bold fs-5 mt-4"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            Now on AutoPilot
          </p>

          {/* Buttons */}
          <div className="d-flex justify-content-center mt-4">
            <div
              className="d-flex flex-column flex-sm-row gap-3"
              style={{ width: "350px" }}
            >
              <a
                className="btn btn-primary w-100 p-2 custom-btn-1"
                style={{ backgroundColor: "#038ff7", borderColor: "#038ff7" }}
                data-aos="zoom-in"
                data-aos-delay="600"
                href="https://tally.so/r/3EjP64"
                target="_blank"
                rel="noopener noreferrer"
              >
                Join the Waitlist
              </a>

              <a
                className="btn btn-outline-primary w-100 p-2 custom-btn-2"
                style={{
                  color: "#038ff7",
                  borderColor: "#038ff7",
                }}
                data-aos="zoom-in"
                data-aos-delay="700"
                href="https://calendly.com/hobstudio-tv/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a Call
              </a>
            </div>
          </div>
        </section>

        {/* VIDEO SECTION */}
        <section className="py-5 px-3 container">
          <div className="container">
            <p
              className="text-start fw-semibold mb-3 text-secondary fs-6 fs-md-5"
              data-aos="fade-up"
            >
              Description video
            </p>

            <div className="position-relative w-100" data-aos="fade-up">
              <video
                ref={videoRef}
                className="w-100 rounded-3"
                src={video}
                autoPlay
                loop
                muted={isMuted} // use state to control mute
                playsInline
              />

              {/* Mute/Unmute button on top-right */}
              <button
                onClick={toggleMute}
                className="position-absolute top-0 end-0 m-3 btn btn-sm btn-dark rounded-circle d-flex justify-content-center align-items-center"
                style={{ width: "40px", height: "40px", opacity: 0.8 }}
              >
                {isMuted ? (
                  // Show "sound off" icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M16.5 12L19 14.5v-5zM5 9v6h4l5 5V4L9 9H5z" />
                  </svg>
                ) : (
                  // Show "sound on" icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                  </svg>
                )}
              </button>

              {/* Progress bar */}
              <div
                className="position-absolute bottom-0 start-0 w-100"
                style={{ height: "6px", backgroundColor: "#ddd" }}
              >
                <div
                  className="bg-primary"
                  style={{
                    width: `${progress}%`,
                    height: "100%",
                    transition: "width 0.1s linear",
                  }}
                ></div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS SECTION */}
        <section
          className="text-center container"
          style={{ marginTop: "10rem", marginBottom: "10rem" }}
        >
          <div className="container px-3">
            {/* Heading */}
            <h2
              className="fw-bold mb-5 mb-md-6 fs-1 fs-md-2 fs-lg-1 text-dark"
              data-aos="fade-up"
            >
              How it Works?
            </h2>

            {/* Grid */}
            <div className="row g-4 g-md-5">
              {/* Item 1 */}
              <div
                className="col-12 col-sm-6 col-lg-3 d-flex flex-column px-2"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <div className="how-card d-flex flex-column h-100">
                  <div className="d-flex justify-content-end mb-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      id="Layer_1"
                      version="1.1"
                      viewBox="0 0 50 50"
                      width={48}
                    >
                      <path
                        fill="#038ff7"
                        d="M44.5 8.15 25.65.32c-.47-.2-1-.2-1.48 0L5.32 8.15c-.72.3-1.19 1-1.19 1.78v9.51c0 13.1 7.92 24.88 20.05 29.83.47.19.99.19 1.45 0a32.21 32.21 0 0 0 20.05-29.83V9.93c0-.78-.47-1.48-1.19-1.78Zm-2.66 11.29c0 11.14-6.54 21.39-16.93 25.96C14.8 40.95 7.98 30.87 7.98 19.44v-8.23l16.93-7.03 16.93 7.03zm-19.06 6.48 8.27-8.27c.75-.75 1.97-.75 2.72 0s.75 1.97 0 2.72L24.14 30c-.75.75-1.97.75-2.72 0l-5.38-5.38c-.75-.75-.75-1.97 0-2.72s1.97-.75 2.72 0z"
                      ></path>
                    </svg>
                  </div>
                  <div className="d-flex flex-column flex-grow-1 justify-content-end">
                    <h3 className="fw-bold fs-md-4 text-start mb-2 text-dark">
                      Subscribe
                    </h3>
                    <p className="fw-semibold fs-5 w-75 fs-md-5 text-start text-secondary">
                      Choose your monthly plan.
                    </p>
                  </div>
                </div>
              </div>

              {/* Item 2 */}
              <div
                className="col-12 col-sm-6 col-lg-3 d-flex flex-column px-2"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <div className="how-card d-flex flex-column h-100">
                  <div className="d-flex justify-content-end mb-3">
                    <svg
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 50 50"
                      width={48}
                    >
                      <defs>
                        <style>
                          {
                            "\n      .st0 {\n        fill: #038ff7;\n      }\n    "
                          }
                        </style>
                      </defs>
                      <path
                        className="st0"
                        d="M8.18,45.02c-.21,0-.42-.06-.62-.22-.72-.58-.33-1.46.39-3.06.33-.73,1.06-2.37,1.03-2.94-.39-.11-1.42.24-3.29,2.2-1.49,1.56-2.74,3.34-2.75,3.36-.27.39-.81.49-1.2.21-.39-.27-.49-.81-.21-1.2.05-.08,1.33-1.9,2.91-3.55.89-.94,3.62-3.79,5.63-2.4,1.26.88.44,2.84-.43,4.79,3.5-2.33,4.8-1.73,6.51-.59.96.64,1.95,1.29,4.05,1.29.48,0,.86.39.86.86s-.39.86-.86.86c-2.63,0-4-.91-5.01-1.58-.77-.51-1.17-.75-1.8-.7-.86.08-2.12.75-3.88,2.05-.31.23-.82.6-1.33.6h0Z"
                      />
                      <path
                        className="st0"
                        d="M47.94,6.22l-1.31-.93c-.75-.54-1.8-.36-2.33.39l-1.3,1.82c-.59.04-1.16.34-1.53.85l-17.28,24.26c-2.32,3.26-2.92,5.92-3.57,7.5-.29.79-.02,1.66.67,2.15.69.49,1.6.46,2.25-.07,1.27-1.13,3.6-2.57,5.91-5.83l15.93-22.37,1.07.76c.13.09.16.27.07.4l-3.73,5.23c-.28.39-.19.93.2,1.21s.93.19,1.21-.2l3.73-5.23c.65-.91.43-2.17-.47-2.81l-1.07-.76.35-.49c.36-.5.47-1.13.31-1.72l1.3-1.82c.54-.75.36-1.8-.39-2.33h0ZM45.67,6.73l1.22.87-1,1.4-1.22-.87,1-1.4ZM42.87,9.36c.1-.14.3-.18.45-.08l1.92,1.37c.14.1.18.3.08.45l-13.55,19.02-2.44-1.74,13.55-19.02ZM28.04,35.36c-1.04,1.46-2.27,2.78-3.67,3.91l-1.93,1.58s-.08.06-.15,0c-.08-.06-.06-.11-.05-.15l.86-2.34c.62-1.69,1.46-3.28,2.5-4.74l2.73-3.83,2.44,1.74-2.73,3.83Z"
                      />
                    </svg>
                  </div>
                  <div className="d-flex flex-column flex-grow-1 justify-content-end">
                    <h3 className="fw-bold fs-md-4 text-start mb-2 text-dark">
                      Send Your Briefs
                    </h3>
                    <p className="fw-semibold fs-5 w-75 fs-md-5 text-start text-secondary">
                      Submit projects instantly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Item 3 */}
              <div
                className="col-12 col-sm-6 col-lg-3 d-flex flex-column px-2"
                data-aos="fade-up"
                data-aos-delay="400"
              >
                <div className="how-card d-flex flex-column h-100">
                  <div className="d-flex justify-content-end mb-3">
                    <svg
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 50 50"
                      width={48}
                    >
                      <defs>
                        <style>
                          {
                            "\n      .st0 {\n        fill: #038ff7;\n      }\n    "
                          }
                        </style>
                      </defs>
                      <path
                        className="st0"
                        d="M26.37,24.41V2.83c0-.73-.59-1.32-1.32-1.32s-1.32.59-1.32,1.32v21.58c-3.06.62-5.38,3.33-5.38,6.57s2.32,5.96,5.38,6.57v9.61c0,.73.59,1.32,1.32,1.32s1.32-.59,1.32-1.32v-9.61c3.06-.62,5.38-3.33,5.38-6.57s-2.31-5.96-5.38-6.57ZM25.05,35.04c-2.24,0-4.06-1.82-4.06-4.06s1.82-4.06,4.06-4.06,4.06,1.82,4.06,4.06-1.82,4.06-4.06,4.06Z"
                      />
                      <path
                        className="st0"
                        d="M10.2,13.33V2.83c0-.73-.59-1.32-1.32-1.32s-1.32.59-1.32,1.32v10.51c-3.06.62-5.38,3.33-5.38,6.57s2.32,5.96,5.38,6.57v20.68c0,.73.59,1.32,1.32,1.32s1.32-.59,1.32-1.32v-20.69c3.06-.62,5.38-3.33,5.38-6.57s-2.32-5.95-5.38-6.56ZM8.88,23.97c-2.24,0-4.06-1.82-4.06-4.06s1.82-4.06,4.06-4.06,4.06,1.82,4.06,4.06-1.83,4.06-4.06,4.06Z"
                      />
                      <path
                        className="st0"
                        d="M42.44,13.33V2.83c0-.73-.59-1.32-1.32-1.32s-1.32.59-1.32,1.32v10.51c-3.06.62-5.38,3.33-5.38,6.57s2.32,5.96,5.38,6.57v20.69c0,.73.59,1.32,1.32,1.32s1.32-.59,1.32-1.32v-20.7c3.06-.62,5.38-3.33,5.38-6.57s-2.31-5.95-5.38-6.56ZM41.12,23.97c-2.24,0-4.06-1.82-4.06-4.06s1.82-4.06,4.06-4.06,4.06,1.82,4.06,4.06-1.82,4.06-4.06,4.06Z"
                      />
                    </svg>
                  </div>
                  <div className="d-flex flex-column flex-grow-1 justify-content-end">
                    <h3 className="fw-bold fs-md-4 text-start mb-2 text-dark">
                      We Create
                    </h3>
                    <p className="fw-semibold fs-5 w-75 fs-md-5 text-start text-secondary">
                      Editing, motion graphics, color grading.
                    </p>
                  </div>
                </div>
              </div>

              {/* Item 4 */}
              <div
                className="col-12 col-sm-6 col-lg-3 d-flex flex-column px-2"
                data-aos="fade-up"
                data-aos-delay="500"
              >
                <div className="how-card d-flex flex-column h-100">
                  <div className="d-flex justify-content-end mb-3">
                    <svg
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 50 50"
                      width={48}
                    >
                      <defs>
                        <style>
                          {
                            "\n      .st0 {\n        fill: #038ff7;\n      }\n    "
                          }
                        </style>
                      </defs>
                      <path
                        className="st0"
                        d="M47.89,6.32c-.4-.4-.94-.63-1.51-.63s-1.1.22-1.51.63l-21.32,21.32-7-7c-.4-.4-.94-.62-1.51-.62s-1.1.22-1.51.62c-.4.4-.63.94-.63,1.51s.22,1.1.63,1.51l8.51,8.51c.39.4.94.62,1.5.62s1.11-.22,1.51-.62l22.83-22.83c.4-.4.62-.94.62-1.5s-.22-1.1-.62-1.51Z"
                      />
                      <path
                        className="st0"
                        d="M43.57,22.86c-1.18,0-2.13.96-2.13,2.14,0,9.84-8.01,17.84-17.84,17.84S5.76,34.84,5.76,25,13.76,7.16,23.6,7.16c2.66,0,5.22.57,7.61,1.7.29.13.59.2.91.2.82,0,1.58-.48,1.93-1.23.25-.51.27-1.09.08-1.63-.19-.53-.58-.96-1.1-1.2-2.96-1.4-6.13-2.11-9.43-2.11C11.41,2.9,1.49,12.81,1.49,25s9.92,22.1,22.11,22.1,22.1-9.92,22.1-22.1c0-1.18-.96-2.14-2.14-2.14Z"
                      />
                    </svg>
                  </div>
                  <div className="d-flex flex-column flex-grow-1 justify-content-end">
                    <h3 className="fw-bold fs-md-4 text-start mb-2 text-dark">
                      Review & Approve
                    </h3>
                    <p className="fw-semibold fs-5 w-75 fs-md-5 text-start text-secondary">
                      Fast revisions, final delivery.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VIDEO CAROUSEL SECTION */}
        {/* <section className="bg-white py-5">
          <div className="container px-3">
            <div className="position-relative">
              <div
                ref={scrollRef}
                className="d-flex overflow-auto pb-2"
                style={{
                  scrollBehavior: "smooth",
                  marginLeft: "1rem", 
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {[
                  "https://www.w3schools.com/html/mov_bbb.mp4",
                  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
                  "https://www.w3schools.com/html/movie.mp4",
                  "https://www.w3schools.com/html/mov_bbb.mp4",
                  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
                ].map((src, idx) => (
                  <video
                    key={idx}
                    src={src}
                    muted
                    controls
                    className="rounded shadow-sm flex-shrink-0 me-3"
                    style={{
                      width: "250px",
                      height: "400px",
                      objectFit: "cover",
                    }}
                  />
                ))}
              </div>

              <button
                className="btn btn-primary rounded-circle position-absolute top-50 translate-middle-y"
                style={{ right: "-20px", width: "48px", height: "48px" }}
                onClick={scrollRight}
                aria-label="Scroll right"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.854 4.146a.5.5 0 0 1 .638-.057l.07.057 4 4a.5.5 0 0 1 .057.638l-.057.07-4 4a.5.5 0 0 1-.765-.638l.057-.07L10.293 8 6.854 4.854a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </section> */}

        <VideoCarousel />

        {/* WHY SUBSCRIPTION */}
        <section
          class="py-5 py-md-5 bg-light"
          style={{ marginTop: "5rem", marginBottom: "5rem" }}
        >
          <div class="container px-3">
            <h2
              class="text-center mb-4 mb-md-5 fw-bold fs-2 fs-md-3 fs-lg-1"
              data-aos="fade-up"
            >
              Why Subscription?
            </h2>

            <div class="row g-5">
              <div class="col-12 col-lg-6">
                <h3
                  class="fw-bold mb-4 fs-4"
                  data-aos="fade-right"
                  data-aos-delay="200"
                >
                  Traditional Projects
                </h3>

                <div class="d-flex flex-column gap-4">
                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="300"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 122.879 122.879"
                    >
                      <path
                        fill="#FF4141"
                        fillRule="evenodd"
                        d="M61.44 0c33.933 0 61.439 27.507 61.439 61.439s-27.506 61.439-61.439 61.439C27.507 122.879 0 95.372 0 61.439S27.507 0 61.44 0m12.011 39.151a7.01 7.01 0 0 1 9.986-.027c2.764 2.776 2.775 7.292.027 10.083L71.4 61.445l12.076 12.249c2.729 2.77 2.689 7.257-.08 10.022-2.773 2.765-7.23 2.758-9.955-.013L61.446 71.54 49.428 83.728a7.01 7.01 0 0 1-9.986.027c-2.763-2.776-2.776-7.293-.027-10.084L51.48 61.434 39.403 49.185c-2.728-2.769-2.689-7.256.082-10.022s7.229-2.758 9.953.013l11.997 12.165z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">Unpredictable Costs</h4>
                      <p class="text-muted mb-0">
                        Project quotes vary wildly, making budgeting impossible.
                      </p>
                    </div>
                  </div>

                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 122.879 122.879"
                    >
                      <path
                        fill="#FF4141"
                        fillRule="evenodd"
                        d="M61.44 0c33.933 0 61.439 27.507 61.439 61.439s-27.506 61.439-61.439 61.439C27.507 122.879 0 95.372 0 61.439S27.507 0 61.44 0m12.011 39.151a7.01 7.01 0 0 1 9.986-.027c2.764 2.776 2.775 7.292.027 10.083L71.4 61.445l12.076 12.249c2.729 2.77 2.689 7.257-.08 10.022-2.773 2.765-7.23 2.758-9.955-.013L61.446 71.54 49.428 83.728a7.01 7.01 0 0 1-9.986.027c-2.763-2.776-2.776-7.293-.027-10.084L51.48 61.434 39.403 49.185c-2.728-2.769-2.689-7.256.082-10.022s7.229-2.758 9.953.013l11.997 12.165z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">Long Waiting Times</h4>
                      <p class="text-muted mb-0">
                        Weeks to find the right freelancer, then weeks for
                        delivery.
                      </p>
                    </div>
                  </div>

                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 122.879 122.879"
                    >
                      <path
                        fill="#FF4141"
                        fillRule="evenodd"
                        d="M61.44 0c33.933 0 61.439 27.507 61.439 61.439s-27.506 61.439-61.439 61.439C27.507 122.879 0 95.372 0 61.439S27.507 0 61.44 0m12.011 39.151a7.01 7.01 0 0 1 9.986-.027c2.764 2.776 2.775 7.292.027 10.083L71.4 61.445l12.076 12.249c2.729 2.77 2.689 7.257-.08 10.022-2.773 2.765-7.23 2.758-9.955-.013L61.446 71.54 49.428 83.728a7.01 7.01 0 0 1-9.986.027c-2.763-2.776-2.776-7.293-.027-10.084L51.48 61.434 39.403 49.185c-2.728-2.769-2.689-7.256.082-10.022s7.229-2.758 9.953.013l11.997 12.165z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">Inconsistent Quality</h4>
                      <p class="text-muted mb-0">
                        Different editors mean different styles and standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-lg-6">
                <h3
                  class="fw-bold mb-4 fs-4"
                  data-aos="fade-right"
                  data-aos-delay="1000"
                >
                  Our Subscription
                </h3>

                <div class="d-flex flex-column gap-4">
                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="1200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 96 96"
                    >
                      <path
                        fill="#6BBE66"
                        fillRule="evenodd"
                        d="M48 0c26.51 0 48 21.49 48 48S74.51 96 48 96 0 74.51 0 48 21.49 0 48 0M26.764 49.277c.644-3.734 4.906-5.813 8.269-3.79q.459.274.867.646l.026.025c1.509 1.446 3.2 2.951 4.876 4.443l1.438 1.291 17.063-17.898c1.019-1.067 1.764-1.757 3.293-2.101 5.235-1.155 8.916 5.244 5.206 9.155L46.536 63.366c-2.003 2.137-5.583 2.332-7.736.291-1.234-1.146-2.576-2.312-3.933-3.489-2.35-2.042-4.747-4.125-6.701-6.187-1.173-1.172-1.679-3.091-1.402-4.704"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">
                        Predictable Monthly Rate
                      </h4>
                      <p class="text-muted mb-0">
                        One flat fee covers all your post-production needs.
                      </p>
                    </div>
                  </div>

                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="1400"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 96 96"
                    >
                      <path
                        fill="#6BBE66"
                        fillRule="evenodd"
                        d="M48 0c26.51 0 48 21.49 48 48S74.51 96 48 96 0 74.51 0 48 21.49 0 48 0M26.764 49.277c.644-3.734 4.906-5.813 8.269-3.79q.459.274.867.646l.026.025c1.509 1.446 3.2 2.951 4.876 4.443l1.438 1.291 17.063-17.898c1.019-1.067 1.764-1.757 3.293-2.101 5.235-1.155 8.916 5.244 5.206 9.155L46.536 63.366c-2.003 2.137-5.583 2.332-7.736.291-1.234-1.146-2.576-2.312-3.933-3.489-2.35-2.042-4.747-4.125-6.701-6.187-1.173-1.172-1.679-3.091-1.402-4.704"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">
                        Lightning Fast Turnaround
                      </h4>
                      <p class="text-muted mb-0">
                        5–7 day delivery on most projects, with rush options.
                      </p>
                    </div>
                  </div>

                  <div
                    class="d-flex align-items-start gap-3"
                    data-aos="fade-right"
                    data-aos-delay="1600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      id="Layer_1"
                      width="24"
                      height="24"
                      x="0"
                      y="0"
                      version="1.1"
                      viewBox="0 0 96 96"
                    >
                      <path
                        fill="#6BBE66"
                        fillRule="evenodd"
                        d="M48 0c26.51 0 48 21.49 48 48S74.51 96 48 96 0 74.51 0 48 21.49 0 48 0M26.764 49.277c.644-3.734 4.906-5.813 8.269-3.79q.459.274.867.646l.026.025c1.509 1.446 3.2 2.951 4.876 4.443l1.438 1.291 17.063-17.898c1.019-1.067 1.764-1.757 3.293-2.101 5.235-1.155 8.916 5.244 5.206 9.155L46.536 63.366c-2.003 2.137-5.583 2.332-7.736.291-1.234-1.146-2.576-2.312-3.933-3.489-2.35-2.042-4.747-4.125-6.701-6.187-1.173-1.172-1.679-3.091-1.402-4.704"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="fw-bold fs-5 mb-1">
                        Studio-Grade Consistency
                      </h4>
                      <p class="text-muted mb-0">
                        Same team, same standards, same quality every time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CHOOSE YOUR PACKAGE */}
        <section
          class="py-5 py-md-5 bg-light"
          style={{ marginTop: "5rem", marginBottom: "5rem" }}
        >
          <div class="container px-3">
            <h2
              class="text-center fw-bold mb-4 mb-md-5 fs-2 fs-md-3 fs-lg-1"
              data-aos="fade-up"
            >
              Choose Your Package
            </h2>

            <div class="row g-4">
              <div
                class="col-12 col-sm-6 col-lg-3"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <div class="plan-card bg-white rounded-4 p-4 h-100 shadow-sm">
                  <h3 class="fw-semibold mb-3 fs-4">Starter Pack</h3>
                  <p class="text-muted small mb-4">
                    Perfect for small businesses and content creators just
                    getting started.
                  </p>
                  <hr class="border-secondary mb-3" />
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="fw-bold mb-0 fs-6">10 credits</p>
                    <span class="fs-3 text-primary">+</span>
                  </div>
                </div>
              </div>

              <div
                class="col-12 col-sm-6 col-lg-3"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                <div class="plan-card bg-white rounded-4 p-4 h-100 shadow-sm border border-primary position-relative">
                  <div class="position-absolute top-0 start-50 translate-middle bg-primary text-white rounded-pill px-3 py-1 small fw-medium">
                    Most popular
                  </div>
                  <h3 class="fw-semibold mb-3 fs-4">Pro Pack</h3>
                  <p class="text-muted small mb-4">
                    Ideal for agencies and growing brands with regular content
                    needs.
                  </p>
                  <hr class="border-secondary mb-3" />
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="fw-bold mb-0 fs-6">25 credits</p>
                    <span class="fs-3 text-primary">+</span>
                  </div>
                </div>
              </div>

              <div
                class="col-12 col-sm-6 col-lg-3"
                data-aos="zoom-in"
                data-aos-delay="600"
              >
                <div class="plan-card bg-white rounded-4 p-4 h-100 shadow-sm">
                  <h3 class="fw-semibold mb-3 fs-4">Business Pack</h3>
                  <p class="text-muted small mb-4">
                    For large teams requiring high-volume, priority production
                    support.
                  </p>
                  <hr class="border-secondary mb-3" />
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="fw-bold mb-0 fs-6">50 credits</p>
                    <span class="fs-3 text-primary">+</span>
                  </div>
                </div>
              </div>

              <div
                class="col-12 col-sm-6 col-lg-3"
                data-aos="zoom-in"
                data-aos-delay="800"
              >
                <div class="plan-card bg-white rounded-4 p-4 h-100 shadow-sm">
                  <h3 class="fw-semibold mb-3 fs-4">VIP Pack</h3>
                  <p class="text-muted small mb-4">
                    For big agencies running multiple campaigns needing ultimate
                    flexibility and capacity.
                  </p>
                  <hr class="border-secondary mb-3" />
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="fw-bold mb-0 fs-6">80 credits</p>
                    <span class="fs-3 text-primary">+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUSTED BY LEADING BRANDS */}
        <section
          class="py-5 bg-white"
          style={{ marginTop: "5rem", marginBottom: "5rem" }}
        >
          <div class="container text-center">
            <h2 class="fw-bold mb-5 fs-2" data-aos="fade-up">
              Trusted by Leading Brands
            </h2>

            <div class="row g-4 justify-content-center">
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <img
                  src={ruckstuhl}
                  alt="Brand 1"
                  class="brand-logo img-fluid"
                />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                <img src={bossard} alt="Brand 2" class="brand-logo img-fluid" />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="600"
              >
                <img
                  src={alpstudios}
                  alt="Brand 3"
                  class="brand-logo img-fluid"
                />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="800"
              >
                <img src={maru} alt="Brand 4" class="brand-logo img-fluid" />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="1000"
              >
                <img src={clyde} alt="Brand 5" class="brand-logo img-fluid" />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="1200"
              >
                <img src={liini} alt="Brand 6" class="brand-logo img-fluid" />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="1400"
              >
                <img src={fibula} alt="Brand 7" class="brand-logo img-fluid" />
              </div>
              <div
                class="col-6 col-md-3 mb-4"
                data-aos="zoom-in"
                data-aos-delay="1600"
              >
                <img src={spitex} alt="Brand 8" class="brand-logo img-fluid" />
              </div>
            </div>
          </div>
        </section>

        {/* LIMITED AVAILABILITY */}
        <section
          className="py-5 py-md-6"
          style={{ marginTop: "5rem", marginBottom: "5rem" }}
        >
          <div
            className="container mx-auto px-3 px-sm-0"
            style={{ maxWidth: "960px" }}
          >
            <div className="bg-light rounded-4 text-center p-1 p-md-5 ">
              <div
                className="how-card"
                style={{ paddingTop: "5rem", paddingBottom: "5rem" }}
              >
                <div
                  className="d-flex justify-content-center align-items-center mb-4 text-primary fw-semibold"
                  style={{ color: "#038ff7" }}
                  data-aos="zoom-in"
                >
                  <svg
                    className="me-2"
                    width="34"
                    height="34"
                    fill="#038ff7"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                  <p
                    className="fw-semibold fs-5 mb-0"
                    style={{ color: "#038ff7", marginLeft: "10px" }}
                  >
                    Limited Availability
                  </p>
                </div>

                <h1
                  className="fw-semibold mb-4 fs-1 fs-md-2 fs-lg-1 mx-auto"
                  style={{ maxWidth: "32rem" }}
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  Limited spots per month to maintain quality.
                </h1>

                <p
                  className="text-muted mb-4 fw-semibold mx-auto"
                  style={{ fontSize: "0.875rem", maxWidth: "32rem" }}
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  We only take on a select number of clients each month to
                  ensure every project receives our full attention and meets our
                  high standards.
                </p>

                <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-2 gap-sm-3">
                  <a
                    href="https://tally.so/r/3EjP64"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-lg rounded-pill btn-left text-center"
                    data-aos="zoom-in"
                    data-aos-delay="800"
                  >
                    Join the Waitlist
                  </a>

                  <a
                    href="https://calendly.com/hobstudio-tv/new-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-lg rounded-pill btn-right text-center"
                    data-aos="zoom-in"
                    data-aos-delay="1000"
                  >
                    Book a call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Last */}
        <section
          class="bg-light py-5 py-md-5 text-center px-3"
          style={{ marginTop: "5rem", marginBottom: "5rem" }}
        >
          <div class="container" style={{ maxwidth: "768px" }}>
            <h3
              class="fw-semibold fs-1 mb-3 fs-md-3 fs-lg-1"
              data-aos="fade-up"
            >
              Ready to Level Up Your Video Content?
            </h3>
            <h4
              class="fw-semibold text-muted fs-5 fs-md-5"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Join the brands that trust HoB Studio for their post-production
              needs.
            </h4>
          </div>
        </section>
      </div>
    </>
  );
}

export default Subscribe;
